import { useState } from 'react'
import { Pencil, Trash2, X, Check, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { showErrorToast, showSuccessToast } from '@/hooks/use-toast'
import {
  useCreateInvoiceNote,
  useDeleteInvoiceNote,
  useInvoiceNotes,
  useUpdateInvoiceNote,
} from '@/hooks/useInvoiceNotes'
import type { InvoiceNote } from '@/services/invoiceNotes'

interface NotesPanelProps {
  invoiceId: string
  hideHeader?: boolean
  onMutate?: () => void
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

function isEdited(n: InvoiceNote): boolean {
  return new Date(n.updated_at).getTime() - new Date(n.created_at).getTime() > 2000
}

export function NotesPanel({ invoiceId, hideHeader = false, onMutate }: NotesPanelProps) {
  const { data: notes, isLoading, isError } = useInvoiceNotes(invoiceId)
  const createMut = useCreateInvoiceNote(invoiceId)
  const updateMut = useUpdateInvoiceNote(invoiceId)
  const deleteMut = useDeleteInvoiceNote(invoiceId)

  const [draft, setDraft] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingDraft, setEditingDraft] = useState('')

  const submitNew = async () => {
    const body = draft.trim()
    if (!body) return
    try {
      await createMut.mutateAsync(body)
      setDraft('')
      showSuccessToast('Note added')
      onMutate?.()
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Failed to add note')
    }
  }

  const startEdit = (n: InvoiceNote) => {
    setEditingId(n.id)
    setEditingDraft(n.body)
  }

  const submitEdit = async () => {
    const body = editingDraft.trim()
    if (!body || !editingId) return
    try {
      await updateMut.mutateAsync({ noteId: editingId, body })
      setEditingId(null)
      setEditingDraft('')
      showSuccessToast('Note updated')
      onMutate?.()
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Failed to update note')
    }
  }

  const remove = async (id: string) => {
    try {
      await deleteMut.mutateAsync(id)
      showSuccessToast('Note deleted')
      onMutate?.()
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Failed to delete note')
    }
  }

  return (
    <div className="flex flex-col max-h-[460px]">
      {!hideHeader && (
        <div className="px-4 py-3 border-b border-neutral-200">
          <h3 className="text-sm font-semibold text-neutral-900">Notes</h3>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-4 text-neutral-500">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        ) : isError ? (
          <div className="text-sm text-red-600">Failed to load notes.</div>
        ) : !notes || notes.length === 0 ? (
          <div className="text-sm text-neutral-500">No notes yet.</div>
        ) : (
          notes.map((n) => (
            <div key={n.id} className="rounded-md border border-neutral-200 p-2">
              {editingId === n.id ? (
                <div className="space-y-2">
                  <Textarea
                    value={editingDraft}
                    onChange={(e) => setEditingDraft(e.target.value)}
                    rows={3}
                    autoFocus
                  />
                  <div className="flex justify-end gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingId(null)
                        setEditingDraft('')
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={submitEdit}
                      disabled={updateMut.isLoading || !editingDraft.trim()}
                    >
                      {updateMut.isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="whitespace-pre-wrap text-sm text-neutral-900">
                    {n.body}
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[11px] text-neutral-500">
                    <span>
                      {n.author_email || 'unknown'} ·{' '}
                      {formatTimestamp(n.created_at)}
                      {isEdited(n) && ' · edited'}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => startEdit(n)}
                        className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-neutral-800"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(n.id)}
                        disabled={deleteMut.isLoading}
                        className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      <div className="border-t border-neutral-200 p-3 space-y-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a note…"
          rows={2}
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={submitNew}
            disabled={createMut.isLoading || !draft.trim()}
          >
            {createMut.isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Add note'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
